import React, { useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { Calendar, Info, Save, X } from 'lucide-react';

const CreatePayrollModal = ({ show, handleClose, handleSave, isCreating = false }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setError(''); // Clear error when user changes date
  };

  const handleSaveClick = async () => {
    if (!selectedDate) {
      setError('Vänligen välj ett utbetalningsdatum');
      return;
    }

    const selectedDateObj = new Date(selectedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDateObj < today) {
      setError('Utbetalningsdatum kan inte vara i det förflutna');
      return;
    }

    // Extract year and month from the payout date
    const [year, month] = selectedDate.split('-').map(num => parseInt(num, 10));

    try {
      await handleSave({
        year: year,
        month: month,
        payoutDate: selectedDate
      });
      handleClose();
      setSelectedDate(new Date().toISOString().split('T')[0]); // Reset form
      setError('');
    } catch (err) {
      setError('Ett fel uppstod när lönekörningen skulle skapas');
    }
  };

  const handleModalClose = () => {
    if (!isCreating) {
      setSelectedDate(new Date().toISOString().split('T')[0]);
      setError('');
      handleClose();
    }
  };

  const getPayoutDateInfo = () => {
    if (!selectedDate) return '';

    const date = new Date(selectedDate);
    const dayOfWeek = date.toLocaleDateString('sv-SE', { weekday: 'long' });
    const formatted = date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `${dayOfWeek}, ${formatted}`;
  };

  return (
    <>
      <style>{`
        .modern-modal .modal-content {
          border: none;
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .modern-modal-header {
          background: white;
          color: #1e293b;
          padding: 2rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modern-modal-header .modal-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .modern-modal-header .modal-title svg {
          color: #3b82f6;
        }

        .modern-modal-header .btn-close {
          background: none;
          border: none;
          color: #64748b;
          opacity: 0.8;
          font-size: 1.5rem;
          cursor: pointer;
          transition: opacity 0.2s ease;
          padding: 0;
          margin: 0;
        }

        .modern-modal-header .btn-close:hover {
          opacity: 1;
          color: #374151;
        }

        .modern-modal-body {
          padding: 2rem;
          background: #f8fafc;
        }

        .modal-info-section {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 2rem;
          display: flex;
          gap: 0.75rem;
        }

        .modal-info-icon {
          color: #3b82f6;
          flex-shrink: 0;
        }

        .modal-info-text {
          color: #1e40af;
          font-size: 0.875rem;
          line-height: 1.5;
        }

        .modern-form-group {
          margin-bottom: 1.5rem;
        }

        .modern-form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .modern-form-input {
          width: 100%;
          padding: 0.75rem;
          border: 2px solid #e5e7eb;
          border-radius: 6px;
          font-size: 1rem;
          transition: all 0.2s ease;
          background: white;
        }

        .modern-form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .modern-form-input:invalid {
          border-color: #dc2626;
        }

        .date-preview {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 1rem;
          margin-top: 0.75rem;
        }

        .date-preview-label {
          font-size: 0.75rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
          text-transform: uppercase;
          font-weight: 500;
          letter-spacing: 0.5px;
        }

        .date-preview-value {
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
        }

        .error-message {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 0.75rem;
          border-radius: 6px;
          font-size: 0.875rem;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .modern-modal-footer {
          background: white;
          padding: 1.5rem 2rem;
          border-top: 1px solid #e5e7eb;
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .modern-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 120px;
          justify-content: center;
        }

        .btn-secondary-modern {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }

        .btn-secondary-modern:hover:not(:disabled) {
          background: #e2e8f0;
          color: #475569;
        }

        .btn-primary-modern {
          background: #3b82f6;
          color: white;
        }

        .btn-primary-modern:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .btn-primary-modern:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .modern-modal-header,
          .modern-modal-body,
          .modern-modal-footer {
            padding: 1.5rem;
          }

          .modern-modal-footer {
            flex-direction: column;
          }

          .modern-btn {
            width: 100%;
          }
        }
      `}</style>

      <Modal
        className="modern-modal fade"
        size="lg"
        show={show}
        onHide={handleModalClose}
        backdrop={isCreating ? 'static' : true}
        keyboard={!isCreating}
      >
        <Modal.Header className="modern-modal-header">
          <Modal.Title className="modern-modal-title">
            <Calendar size={24} />
            Skapa ny lönekörning
          </Modal.Title>
          {!isCreating && (
            <button
              className="btn-close"
              onClick={handleModalClose}
              aria-label="Stäng"
            >
              <X size={20} />
            </button>
          )}
        </Modal.Header>

        <Modal.Body className="modern-modal-body">
          <div className="modal-info-section">
            <Info size={20} className="modal-info-icon" />
            <div className="modal-info-text">
              <strong>Skapa en ny löneperiod</strong><br />
              Välj det datum då lönerna kommer att betalas ut till de anställda.
              Detta datum används för att planera utbetalningar och kan ändras senare om det behövs.
            </div>
          </div>

          {error && (
            <div className="error-message">
              <X size={16} />
              {error}
            </div>
          )}

          <div className="row">
            <div className="col-md-6">
              <div className="modern-form-group">
                <label className="modern-form-label">
                  Utbetalningsdatum *
                </label>
                <input
                  type="date"
                  className="modern-form-input"
                  value={selectedDate}
                  onChange={handleDateChange}
                  min={new Date().toISOString().split('T')[0]}
                  disabled={isCreating}
                  required
                />
              </div>
            </div>

            <div className="col-md-6">
              {selectedDate && (
                <div className="date-preview">
                  <div className="date-preview-label">Valt datum</div>
                  <div className="date-preview-value">
                    {getPayoutDateInfo()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="modern-modal-footer">
          <button
            className="modern-btn btn-secondary-modern"
            onClick={handleModalClose}
            disabled={isCreating}
          >
            <X size={16} />
            Avbryt
          </button>

          <button
            className="modern-btn btn-primary-modern"
            onClick={handleSaveClick}
            disabled={isCreating || !selectedDate}
          >
            {isCreating ? (
              <>
                <div className="loading-spinner" />
                Skapar...
              </>
            ) : (
              <>
                <Save size={16} />
                Skapa lönekörning
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreatePayrollModal;




// import React, { useState } from 'react';
// import { Modal, Button } from 'react-bootstrap';

// const CreatePayrollModal = ({ show, handleClose, handleSave }) => {
//   const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

//   const handleDateChange = (e) => {
//     setSelectedDate(e.target.value);
//   };

//   const handleSaveClick = () => {
//     handleSave(selectedDate);
//     handleClose();
//   };

//   return (
//     <Modal className='fade bd-example-modal-lg' size='lg' show={show} onHide={handleClose}>
//       <Modal.Header closeButton>
//         <Modal.Title>Ny lönekörning</Modal.Title>
//       </Modal.Header>
//       <Modal.Body>
//         <div className='col-lg-12'>
//           <div className='row'>
//             <div className="col-lg-4">
//               <div>
//                 Välje det datum då löner kommer att betalas ut.
//               </div>
//             </div>
//             <div className="col-xl-4">
//               <div className="form-group mb-3 invoice">
//                 <label>Utbetalningsdatum</label>
//                 <input
//                   size="16"
//                   type="date"
//                   className="form-control"
//                   value={selectedDate}
//                   onChange={handleDateChange}
//                   min={new Date().toISOString().split('T')[0]}
//                 />
//               </div>
//             </div>
//           </div>
//         </div>
//       </Modal.Body>
//       <Modal.Footer>
//         <Button variant="secondary" onClick={handleClose}>
//           Cancel
//         </Button>
//         <Button variant="primary" onClick={handleSaveClick}>
//           Save
//         </Button>
//       </Modal.Footer>
//     </Modal>
//   );
// };

// export default CreatePayrollModal;